import React, { useState } from 'react';
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import SystemIcon from '../components/ui/SystemIcon';
import { CONTACT_LINKS, getSupportMailto } from '../config/contactLinks';
import { flixUi } from '../styles/flixUiTheme';

function LoginPage() {
  const {
    user,
    hasConfig,
    signInWithEmail,
    signInWithGoogle,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const searchParams = new URLSearchParams(location.search);
  const continuePath = (searchParams.get('continue') || '').trim();
  const nextPath = location.state?.from || continuePath || '/profiles';

  if (user) {
    return <Navigate to={nextPath} replace />;
  }

  const handleEmailSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await signInWithEmail(email, password);
      sessionStorage.removeItem('selectedProfile');
      navigate(nextPath, { replace: true });
    } catch (err) {
      setError(err?.message || 'Could not sign in.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = async () => {
    setSubmitting(true);
    setError('');
    try {
      await signInWithGoogle();
      sessionStorage.removeItem('selectedProfile');
      navigate(nextPath, { replace: true });
    } catch (err) {
      setError(err?.message || 'Google sign-in failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      style={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.2rem',
        color: flixUi.text,
        fontFamily: flixUi.fontFamily,
        background: '#000',
      }}
    >
      <style>{`
        .vf-login-input:focus {
          outline: none;
          border-color: rgba(255,255,255,0.58);
          background: rgba(14,14,14,0.92);
        }
        .vf-login-link {
          color: rgba(232,236,244,0.78);
          text-decoration: underline;
          transition: color 180ms ease;
        }
        .vf-login-link:hover,
        .vf-login-link:focus-visible {
          color: rgba(255,255,255,0.98);
        }
      `}</style>

      <video
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center center',
          transform: 'scale(1.04)',
        }}
      >
            <source
          src="https://res.cloudinary.com/da1u9kl5f/video/upload/v1771847329/403_red-theatre-seats-vid_bzpf2w.mp4"
          type="video/mp4"
        />
      </video>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.24), rgba(0,0,0,0.46))',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at 50% 0%, rgba(132,0,14,0.18) 0%, rgba(41,0,6,0.14) 30%, rgba(0,0,0,0.2) 62%, rgba(0,0,0,0.38) 100%)',
        }}
      />

      <header
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 2,
          padding: '1.5rem clamp(1.1rem, 5vw, 3.5rem) 1rem',
          borderBottom: '1px solid rgba(255,255,255,0.18)',
        }}
      >
        <img
          src="https://res.cloudinary.com/da1u9kl5f/image/upload/v1770637993/veeflix_pe6wbz.png"
          alt="VEEFLIX"
          style={{ height: 'clamp(36px, 4vw, 50px)', width: 'auto', objectFit: 'contain' }}
        />
      </header>

      <section
        style={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: 560,
          marginTop: 'clamp(3rem, 12vh, 7rem)',
          padding: '0 1.2rem',
        }}
      >
        <h1
          style={{
            marginTop: 0,
            marginBottom: '0.45rem',
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            lineHeight: 1.05,
            fontWeight: 600,
            letterSpacing: flixUi.titleLetterSpacing,
          }}
        >
          Your Screening Awaits.
        </h1>
        <p style={{ marginTop: 0, color: 'rgba(228,233,240,0.78)', marginBottom: '1.25rem', fontSize: 'clamp(1rem, 1.8vw, 1.2rem)' }}>
          Sign in to continue your premiere.
        </p>

        {!hasConfig ? (
          <p style={{ color: '#fca5a5', marginBottom: '0.9rem', fontSize: '0.86rem' }}>
            Firebase is not configured yet. Add Firebase env vars to continue.
          </p>
        ) : null}

        <form onSubmit={handleEmailSubmit}>
          <input
            className="vf-login-input"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            style={{
              width: '100%',
              marginBottom: '0.72rem',
              padding: '0.95rem 1rem',
              borderRadius: 6,
              border: '1px solid rgba(255,255,255,0.3)',
              background: 'rgba(18,18,18,0.82)',
              color: '#fff',
              transition: 'border-color 180ms ease, background 180ms ease',
              fontSize: '1.05rem',
            }}
            required
          />
          <input
            className="vf-login-input"
            type="password"
            placeholder="Password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            style={{
              width: '100%',
              marginBottom: '0.68rem',
              padding: '0.95rem 1rem',
              borderRadius: 6,
              border: '1px solid rgba(255,255,255,0.3)',
              background: 'rgba(18,18,18,0.82)',
              color: '#fff',
              transition: 'border-color 180ms ease, background 180ms ease',
              fontSize: '1.05rem',
            }}
            minLength={6}
            required
          />
          <p style={{ minHeight: '1.2rem', marginTop: 0, marginBottom: '0.35rem', color: '#ff7f8b', fontSize: '0.9rem' }}>
            {error || ''}
          </p>
          <button
            type="submit"
            disabled={submitting || !hasConfig}
            style={{
              width: '100%',
              padding: '0.92rem 1rem',
              borderRadius: 8,
              border: 0,
              background: '#e50914',
              color: '#fff',
              fontWeight: 700,
              fontSize: '1.05rem',
              cursor: 'pointer',
              transition: 'opacity 180ms ease',
              opacity: submitting || !hasConfig ? 0.6 : 1,
            }}
          >
            Continue
          </button>
        </form>

        <button
          type="button"
          onClick={handleGoogle}
          disabled={submitting || !hasConfig}
          style={{
            width: '100%',
            marginTop: '0.65rem',
            padding: '0.8rem 0.9rem',
            borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.2)',
            background: 'transparent',
            color: 'rgba(247,249,252,0.92)',
            fontWeight: 600,
            cursor: 'pointer',
            opacity: submitting || !hasConfig ? 0.6 : 1,
          }}
        >
          Continue with Google
        </button>

        <p style={{ marginTop: '1rem', marginBottom: 0, color: 'rgba(221,226,236,0.72)', fontSize: '0.9rem' }}>
          Access is granted via private invite.
        </p>
        <p style={{ marginTop: '0.35rem', marginBottom: 0, color: 'rgba(183,189,200,0.74)', fontSize: '0.88rem' }}>
          Need help?
          {' '}
          <a className="vf-login-link" href={CONTACT_LINKS.dmUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <SystemIcon icon="sparkles" size={12} alt="" tone="muted" />
            DM us
          </a>
          {' · '}
          <a className="vf-login-link" href={getSupportMailto()} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <SystemIcon icon="mail" size={12} alt="" tone="muted" />
            Email support
          </a>
        </p>

        <p style={{ marginTop: '0.8rem', marginBottom: 0, fontSize: '0.84rem' }}>
          <Link to="/" style={{ color: 'rgba(155,162,176,0.85)', textDecoration: 'underline' }}>Back to home</Link>
        </p>
      </section>
    </main>
  );
}

export default LoginPage;
